import { useCallback, useRef, useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../lib/shadcn/alert-dialog'
import { fieldListLabel } from '../../utils/marketing'

/**
 * The warning before an edit to approved content saves.
 *
 * Migration 0410 lets an approved post be edited, and an edit to anything the
 * approval covered sends it back to Draft for a new approval. The database
 * refuses that edit unless the call says it was confirmed, so this dialog is
 * the only way to say yes, and there is no path that reopens an approval
 * without a person having read this sentence first.
 *
 * Promise-shaped so a field's blur handler can simply await the answer. One
 * question at a time: a second ask while one is open answers the first "no",
 * because two stacked dialogs about the same post is how the wrong one gets
 * clicked.
 */
export function useReopenConfirm() {
  const [fields, setFields] = useState<string[] | null>(null)
  const resolver = useRef<((ok: boolean) => void) | null>(null)

  const answer = useCallback((ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setFields(null)
  }, [])

  const ask = useCallback((what: string[]) => {
    resolver.current?.(false)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
      setFields(what)
    })
  }, [])

  const dialog = (
    <AlertDialog open={fields !== null} onOpenChange={(o) => !o && answer(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>This is approved. Change it anyway?</AlertDialogTitle>
          <AlertDialogDescription>
            {fields
              ? `Changing the ${fieldListLabel(fields)} sends this back to Draft. The approval stops covering it, and it has to be sent for approval again before it can go out.`
              : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => answer(false)}>Keep it as approved</AlertDialogCancel>
          <AlertDialogAction onClick={() => answer(true)}>Change it and send back to Draft</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { ask, dialog }
}
